import { ButtonHTMLAttributes, ReactNode, useState } from 'react';
import { Spinner } from '../feedback/Spinner';

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 002.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

const AppleMark = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.04c-.03-2.6 2.13-3.85 2.22-3.91-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.4-.9-1.75.03-3.36 1.02-4.26 2.58-1.82 3.15-.47 7.81 1.3 10.37.86 1.25 1.89 2.66 3.24 2.61 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.38.81 1.4-.02 2.28-1.28 3.13-2.54.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.72-1.04-2.75-4.13zM14.56 4.6c.72-.87 1.2-2.08 1.07-3.29-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.08 3.18 1.15.09 2.32-.58 3.03-1.45z" />
  </svg>
);

/**
 * OAuthButton — full-width social sign-in button. A soft-cornered (12px) card
 * on the sunken --bg fill with a brand mark and a label. Disabled dims to 40%.
 *
 * provider: 'google' | 'apple'. Pass `label` to override the default text.
 * tone: 'default' (theme --bg/--line card) — or 'inverted': solid cream pill,
 * no border, for a fixed dark/violet backdrop that doesn't track the app's
 * light/dark theme (e.g. auth screens).
 */
export interface OAuthButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Which provider mark + default label to show. Default 'google'. */
  provider?: 'google' | 'apple';
  /** Override the default label text. */
  label?: ReactNode;
  disabled?: boolean;
  /** Shows an inline spinner in place of the brand mark and disables the button. */
  loading?: boolean;
  onClick?: () => void;
  /** 'default' (theme card) or 'inverted' (solid cream pill). Default 'default'. */
  tone?: 'default' | 'inverted';
}

export function OAuthButton({ provider = 'google', label, disabled = false, loading = false, onClick, tone = 'default', style, ...props }: OAuthButtonProps) {
  const [hover, setHover] = useState(false);
  const isDisabled = disabled || loading;
  const inverted = tone === 'inverted';
  const marks = { google: <GoogleMark />, apple: <AppleMark /> };
  const defaultLabel = { google: 'Continue with Google', apple: 'Continue with Apple' }[provider];

  return (
    <button
      disabled={isDisabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        width: '100%',
        height: 48,
        borderRadius: inverted ? 'var(--r-pill)' : 12,
        border: inverted ? 'none' : '1px solid var(--line)',
        background: inverted ? 'rgb(var(--cream-bright))' : (hover && !isDisabled ? 'var(--surface)' : 'var(--bg)'),
        color: inverted ? 'rgb(var(--ink))' : (isDisabled ? 'var(--text-dim)' : 'var(--text)'),
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        fontWeight: 500,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.4 : 1,
        transition: 'background .15s',
        ...style,
      }}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : marks[provider]}
      {label ?? defaultLabel}
    </button>
  );
}

export default OAuthButton;
